// ─────────────────────────────────────────────────────────────
// MARINEFORCELED — CENTRALIZED, EDITABLE COMMERCIAL CONFIGURATION
// Every price, bundle, upsell, shipping and warranty rule on the
// site reads from this file. Official launch pricing. Edit here only.
// ─────────────────────────────────────────────────────────────

export type BundleId = 'sys1' | 'sys2' | 'sys4' | 'sys6' | 'sys8' | 'sys12' | 'sys24';
export type WarrantyTier = 'oneYear' | 'lifetime';

export type Bundle = {
  id: BundleId;
  qty: number;
  price: number;
  /** Shopify SKU / variant handle for this exact configuration. */
  sku: string;
  name: string;
  nameEs: string;
  /** Short selector label, e.g. "Starter System". */
  tagline: string;
  taglineEs: string;
  /** Understated merchandising label, or null. */
  badge: 'mostPopular' | 'bestValue' | null;
  suggestedFor: string;
  suggestedForEs: string;
  warranty: WarrantyTier;
  featured: boolean;
  /** Approved product artwork showing exactly `qty` M1 PRO lights. */
  image: string;
  /** Live Shopify variant ID on shop.marineforceled.com. `:1` in a permalink = one complete system. */
  variantId: string;
};

export type UpgradeOffer = {
  /** Primary larger system that REPLACES the current one. */
  primary: BundleId | null;
  /** Optional secondary upgrade. */
  alt: BundleId | null;
  headline: string;
};

/**
 * What each system is designed for, framed around installed positions + charged backups.
 * Systems of 4+ are presented as half installed, half ready.
 */
export const systemPurpose: Record<BundleId, { en: string; es: string }> = {
  sys1: { en: 'Your first lighting position — try M1 PRO on a step, fountain or accent.', es: 'Tu primera posición de luz — prueba el M1 PRO en un escalón, fuente o detalle.' },
  sys2: { en: '1 installed + 1 ready, or two accent positions for a spa or steps.', es: '1 instalada + 1 lista, o dos posiciones para un spa o escalones.' },
  sys4: { en: '2 installed. 2 charged backups. A compact spa or entry, always ready.', es: '2 instaladas. 2 de respaldo cargadas. Un spa o entrada compacta, siempre lista.' },
  sys6: { en: '3 installed. 3 charged backups. Small pools and plunge pools.', es: '3 instaladas. 3 de respaldo cargadas. Piscinas pequeñas y de inmersión.' },
  sys8: { en: '4 installed. 4 ready. Your complete weekly pool lighting system.', es: '4 instaladas. 4 listas. Tu sistema semanal completo para la piscina.' },
  sys12: { en: '6 installed. 6 charged backups. Medium and large pools.', es: '6 instaladas. 6 de respaldo cargadas. Piscinas medianas y grandes.' },
  sys24: { en: '12 installed. 12 ready. The complete lighting & backup system.', es: '12 instaladas. 12 listas. El sistema completo de luz y respaldo.' },
};

export type VolumeTier = { minQty: number; discountPct: number };

export const pricingConfig = {
  currency: 'USD',
  pricesApproved: true,
  /** Official single-light price. All bundle savings are measured against this. */
  unitPrice: 29.95,
  maxIndividualQty: 50,
  /** No quantity discounts outside the official systems. */
  volumeTiers: [{ minQty: 1, discountPct: 0 }] as VolumeTier[],
  /** Default system in the bundle selector. */
  defaultBundle: 'sys8' as BundleId,
  bundles: [
    {
      id: 'sys1',
      qty: 1,
      price: 29.95,
      sku: 'MFL-M1PRO-1',
      name: '1 M1 PRO Light',
      nameEs: '1 luz M1 PRO',
      tagline: 'Single Light',
      taglineEs: 'Luz individual',
      badge: null,
      suggestedFor: 'Accent a step, fountain or small feature',
      suggestedForEs: 'Para un escalón, fuente o detalle pequeño',
      warranty: 'oneYear',
      featured: false,
      image: 'https://cdn.magicpatterns.com/uploads/xiMcCEnXJxfN8xg6KYv9L5/604EDCBC-C1D3-4D3D-85F0-55D71BA46184_(1).png',
      variantId: '67722771398749',
    },
    {
      id: 'sys2',
      qty: 2,
      price: 54.9,
      sku: 'MFL-M1PRO-2',
      name: '2-Light Duo System',
      nameEs: 'Sistema Duo de 2 luces',
      tagline: 'Duo System',
      taglineEs: 'Sistema Duo',
      badge: null,
      suggestedFor: 'Suggested for steps, fountains and small spas',
      suggestedForEs: 'Sugerido para escalones, fuentes y spas pequeños',
      warranty: 'oneYear',
      featured: false,
      image: 'https://cdn.magicpatterns.com/uploads/cwYVroYYquc5t4xh8ruriT/B4E7BBE0-B360-432C-A906-F7F52CD57129.png',
      variantId: '67722791190621',
    },
    {
      id: 'sys4',
      qty: 4,
      price: 99.95,
      sku: 'MFL-M1PRO-4',
      name: '4-Light Starter System',
      nameEs: 'Sistema Starter de 4 luces',
      tagline: 'Starter System',
      taglineEs: 'Sistema Starter',
      badge: null,
      suggestedFor: 'Suggested for spas and compact spaces',
      suggestedForEs: 'Sugerido para spas y espacios compactos',
      warranty: 'lifetime',
      featured: false,
      image: 'https://cdn.magicpatterns.com/uploads/dq7VoKxqc9S6E1Lnp6mXK8/AA8AAAC0-850C-4589-985B-709B12092D77_(1).png',
      variantId: '67722830282845',
    },
    {
      id: 'sys6',
      qty: 6,
      price: 149.95,
      sku: 'MFL-M1PRO-6',
      name: '6-Light Essential System',
      nameEs: 'Sistema Essential de 6 luces',
      tagline: 'Essential System',
      taglineEs: 'Sistema Essential',
      badge: null,
      suggestedFor: 'Suggested for small pools and plunge pools',
      suggestedForEs: 'Sugerido para piscinas pequeñas y de inmersión',
      warranty: 'lifetime',
      featured: false,
      image: 'https://cdn.magicpatterns.com/uploads/1iZcFaueMRi51qDyDm3rPJ/6DA73A2A-7186-4641-8347-E60872D2F950.png',
      variantId: '67722839916637',
    },
    {
      id: 'sys8',
      qty: 8,
      price: 189.95,
      sku: 'MFL-M1PRO-8',
      name: '8-Light Signature System',
      nameEs: 'Sistema Signature de 8 luces',
      tagline: 'Signature System',
      taglineEs: 'Sistema Signature',
      badge: 'mostPopular',
      suggestedFor: 'Suggested for standard pools',
      suggestedForEs: 'Sugerido para piscinas estándar',
      warranty: 'lifetime',
      featured: true,
      image: 'https://cdn.magicpatterns.com/uploads/oauDwWfMvBvQuHvcaUPGXR/39ACE5D3-2B73-4A14-BCFF-61E25F283613.png',
      variantId: '67722845225053',
    },
    {
      id: 'sys12',
      qty: 12,
      price: 269.95,
      sku: 'MFL-M1PRO-12',
      name: '12-Light Complete System',
      nameEs: 'Sistema Complete de 12 luces',
      tagline: 'Complete System',
      taglineEs: 'Sistema Complete',
      badge: null,
      suggestedFor: 'Suggested for medium and large pools',
      suggestedForEs: 'Sugerido para piscinas medianas y grandes',
      warranty: 'lifetime',
      featured: false,
      image: 'https://cdn.magicpatterns.com/uploads/pJZyNB3kyCqT9CtqKpYVtk/983249F8-A595-47F9-A816-B8723B311426.png',
      variantId: '67722848141405',
    },
    {
      id: 'sys24',
      qty: 24,
      price: 479.95,
      sku: 'MFL-M1PRO-24',
      name: '24-Light Ultimate System',
      nameEs: 'Sistema Ultimate de 24 luces',
      tagline: 'Ultimate System',
      taglineEs: 'Sistema Ultimate',
      badge: 'bestValue',
      suggestedFor: 'Suggested for larger pools and extensive lighting projects',
      suggestedForEs: 'Sugerido para piscinas grandes y proyectos extensos',
      warranty: 'lifetime',
      featured: false,
      image: 'https://cdn.magicpatterns.com/uploads/qFmnhfFsbKoztJiCR17igR/52AE5EDF-6EBA-46AF-BF32-1B8293883348.png',
      variantId: '67722849943645',
    },
  ] as Bundle[],
  /** Bundle-exclusive extra lights are disabled: no discounts beyond the official systems. */
  addon: {
    price: 29.95,
    maxQty: 12,
    qualifyingBundles: [] as BundleId[],
    inheritBundleWarranty: true,
  },
  /** Pre-checkout upgrade offers. An upgrade REPLACES the selected system; the customer pays the new system only. */
  upgradeOffers: {
    sys1: { primary: 'sys2', alt: 'sys4', headline: 'Double your lighting possibilities' },
    sys2: { primary: 'sys4', alt: 'sys6', headline: 'Turn your lights into a complete backup system' },
    sys4: { primary: 'sys8', alt: 'sys6', headline: 'Make your pool system always ready' },
    sys6: { primary: 'sys8', alt: 'sys12', headline: 'Get two more lights for just {diff} more' },
    sys8: { primary: 'sys12', alt: 'sys24', headline: 'Complete your next lighting upgrade' },
    sys12: { primary: 'sys24', alt: null, headline: 'Double your complete system' },
    sys24: { primary: null, alt: null, headline: 'You chose our largest system' },
  } as Record<BundleId, UpgradeOffer>,
  promo: {
    active: false,
    label: '',
    pctOff: 0,
  },
  upsell: {
    maxSteps: 2,
  },
};

/**
 * Shipping display. Free standard shipping (contiguous U.S.) is the intended
 * launch policy; it is advertised only once `live` is true, i.e. after the
 * Shopify shipping profile is configured to charge $0.
 */
export const shippingConfig = {
  freeStandardContiguousUS: true,
  live: true,
};

export const freeShippingLive = () => shippingConfig.freeStandardContiguousUS && shippingConfig.live;

export const warrantyConfig = {
  individual: 'oneYear' as WarrantyTier,
  /** Optional lifetime upgrade for 1- and 2-light orders — NOT shown until written terms are approved. */
  lifetimeUpgrade: { price: 19.95, eligibleBundles: ['sys1', 'sys2'] as BundleId[], active: false },
  labels: {
    oneYear: '1-Year Limited Warranty',
    lifetime: 'Limited Lifetime Warranty',
  },
  labelsEs: {
    oneYear: 'Garantía limitada de 1 año',
    lifetime: 'Garantía limitada de por vida',
  },
};

export const includedPerLight = [
  'M1 PRO Wireless RGB Light',
  'Magnetic Mount System base (matched 3.3 in diameter)',
  'USB-C charging cable',
  'Waterproof mounting sealant',
  "Owner's guide",
  'Cleaning cloth for installation',
];

export const includedPerLightEs = [
  'Luz inalámbrica RGB M1 PRO',
  'Base del Magnetic Mount System (mismo diámetro de 3.3 in)',
  'Cable de carga USB-C',
  'Sellador impermeable para la base',
  'Guía del propietario',
  'Paño de limpieza para la instalación',
];

/** Remote controls per order: 1 → 1, 2–5 → 2, 6+ → 3 (1 in use + 2 spares). */
export function remotesForQty(qty: number): number {
  if (qty <= 1) return 1;
  if (qty < 6) return 2;
  return 3;
}

export function remotesLabel(qty: number, lang: 'en' | 'es' = 'en'): string {
  const n = remotesForQty(qty);
  const es = lang === 'es';
  const base = es ? (n === 1 ? 'Control remoto' : 'Controles remotos') : n === 1 ? 'Remote control' : 'Remote controls';
  const detail =
    n === 3
      ? es
        ? ' (1 en uso + 2 de repuesto)'
        : ' (1 in use + 2 spares)'
      : '';
  const aaa = es ? ' · usan 2 AAA, no incluidas' : ' · use 2 AAA, not included';
  return `${n} × ${base}${detail}${aaa}`;
}

export const inclusionsPendingNote = 'Includes USB-C charging cable and waterproof mounting sealant.';

// ─────────────────────────────────────────────────────────────
// COST MODEL — for contribution-margin analysis (internal only).
// All values are null until the owner supplies confirmed figures.
// Margins are NEVER calculated from assumed costs.
// ─────────────────────────────────────────────────────────────
export type CostModel = {
  unitCost: number | null; // landed cost per light + base
  packagingCostPerOrder: number | null;
  packagingCostPerUnit: number | null;
  shippingCostPerOrder: number | null; // first light
  shippingCostPerAdditionalUnit: number | null;
  paymentProcessingPct: number | null;
  paymentProcessingFixed: number | null;
  warrantyAllowancePct: Record<WarrantyTier, number | null>; // % of product cost reserved
};

export const costModel: CostModel = {
  unitCost: null,
  packagingCostPerOrder: null,
  packagingCostPerUnit: null,
  shippingCostPerOrder: null,
  shippingCostPerAdditionalUnit: null,
  paymentProcessingPct: null,
  paymentProcessingFixed: null,
  warrantyAllowancePct: { oneYear: null, lifetime: null },
};
